import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { ArrowLeft, Coins, Gift, Ticket, Clock, LogIn, Trophy, Volume2, VolumeX, Sparkles as SparkIcon } from "lucide-react";
import { useMember } from "@/contexts/MemberContext";
import { memberApi, formatMemberError } from "@/lib/memberClient";
import { api } from "@/lib/apiClient";
import { BRAND } from "@/constants/brand";
import SpinWheel from "@/components/SpinWheel";
import PandaMascot from "@/components/PandaMascot";

const BG_EMOJIS = ["🍭", "🍓", "🍫", "🪙", "🍿", "✨", "🐼", "🍬", "🎊", "🎁"];

export default function MemberSpin() {
  const { member, loading, setMember } = useMember();
  const [wheel, setWheel] = useState(null);
  const [eligibility, setEligibility] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [targetIndex, setTargetIndex] = useState(null);
  const [spinHistory, setSpinHistory] = useState([]);
  const [mood, setMood] = useState("waiting");
  const [flyingCoins, setFlyingCoins] = useState(false);
  const [soundOn, setSoundOn] = useState(() => (typeof window !== "undefined" ? localStorage.getItem("pp_spin_sound") !== "0" : true));
  const walletRef = useRef(null);
  const nav = useNavigate();

  // Public wheel load (works for guests)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/spin/wheels/active");
        if (data?.length) setWheel(data[0]);
      } catch { /* ignore */ }
    })();
  }, []);

  const loadMemberData = async () => {
    if (!wheel || !member) return;
    try {
      const [{ data: elig }, { data: hist }] = await Promise.all([
        memberApi.get(`/member/spin/eligibility/${wheel.id}`),
        memberApi.get("/member/spins"),
      ]);
      setEligibility(elig);
      setSpinHistory(hist);
    } catch { /* ignore */ }
  };

  useEffect(() => { loadMemberData(); /* eslint-disable-next-line */ }, [wheel?.id, member?.id]);

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    localStorage.setItem("pp_spin_sound", next ? "1" : "0");
  };

  const doSpin = async () => {
    if (!member) { nav("/member/login"); return; }
    if (!wheel || spinning) return;
    if (!eligibility?.eligible) { toast.error(eligibility?.reason || "You can't spin right now."); return; }

    setSpinning(true);
    setMood("excited");
    setResult(null);
    setShowResult(false);
    try {
      const { data } = await memberApi.post("/member/spin", { wheel_id: wheel.id });
      setResult(data);
      setTargetIndex(data.segment_index);
    } catch (e) {
      setSpinning(false);
      setMood("waiting");
      toast.error(formatMemberError(e));
    }
  };

  const onSpinComplete = () => {
    setSpinning(false);
    setShowResult(true);
    if (result?.is_win) {
      setMood("celebrating");
      confetti({ particleCount: 180, spread: 120, origin: { y: 0.55 }, colors: ["#FFC93C", "#FF4F5A", "#FFF8F0", wheel?.theme_color || "#FFC93C"] });
      setTimeout(() => confetti({ particleCount: 90, spread: 90, angle: 60, origin: { x: 0, y: 0.7 } }), 200);
      setTimeout(() => confetti({ particleCount: 90, spread: 90, angle: 120, origin: { x: 1, y: 0.7 } }), 400);
      if (result.segment_type === "coins") {
        setFlyingCoins(true);
        setTimeout(() => setFlyingCoins(false), 1800);
        // Update balance in context
        if (result.prize_meta?.new_balance !== undefined && setMember) {
          setMember((m) => (m ? { ...m, pop_coins: result.prize_meta.new_balance } : m));
        }
      }
    } else {
      setMood("waving");
    }
    setTimeout(() => setMood("waiting"), 5000);
    loadMemberData();
  };

  if (loading) return <div className="min-h-screen bg-[#2B2B2B] grid place-items-center text-brand-cream">Loading…</div>;

  return (
    <div data-testid="member-spin-page" className="min-h-screen relative overflow-hidden" style={{
      background: "linear-gradient(135deg, #1a0f1f 0%, #2d1b3d 25%, #3a2318 50%, #2B2B2B 100%)",
    }}>
      {/* ---------- Animated background ---------- */}
      <AnimatedBackground />

      {/* ---------- Header ---------- */}
      <header className="relative z-30 flex items-center justify-between px-4 sm:px-6 lg:px-12 py-5 text-brand-cream">
        <Link to="/" className="flex items-center gap-3">
          <img src={BRAND.logoUrl} alt="Pop Panda" className="h-9 w-9 bg-brand-cream rounded-full p-1.5" />
          <div className="font-display text-xl">Pop Panda Club</div>
        </Link>

        <div className="flex items-center gap-2">
          {/* Wallet chip (target of flying coins) */}
          {member && (
            <motion.div
              ref={walletRef}
              animate={flyingCoins ? { scale: [1, 1.15, 1], rotate: [0, 5, -5, 0] } : {}}
              transition={{ duration: 0.6 }}
              className="hidden sm:flex items-center gap-2 rounded-full bg-white/15 border border-brand-yellow/40 backdrop-blur-lg px-4 py-1.5 shadow-lg"
              data-testid="spin-wallet-chip"
            >
              <Coins size={16} className="text-brand-yellow" />
              <motion.span
                key={member.pop_coins}
                initial={{ scale: 1.4, color: "#FFC93C" }}
                animate={{ scale: 1, color: "#FFF8F0" }}
                transition={{ duration: 0.6 }}
                className="font-mono text-sm font-semibold"
              >{member.pop_coins ?? 0}</motion.span>
            </motion.div>
          )}
          <button onClick={toggleSound} data-testid="spin-sound-toggle" className="rounded-full bg-white/10 border border-white/15 backdrop-blur-lg h-9 w-9 grid place-items-center hover:bg-white/20 transition" aria-label="Toggle sound">
            {soundOn ? <Volume2 size={15} /> : <VolumeX size={15} />}
          </button>
          {member ? (
            <Link to="/member/dashboard" data-testid="spin-back-dashboard" className="rounded-full bg-white/10 border border-white/15 backdrop-blur-lg px-4 py-1.5 text-xs uppercase tracking-widest hover:bg-white/20 transition inline-flex items-center gap-1.5">
              <ArrowLeft size={14} /> Dashboard
            </Link>
          ) : (
            <Link to="/member/login" data-testid="spin-signin-cta" className="rounded-full bg-brand-yellow text-brand-charcoal px-4 py-1.5 text-xs font-medium uppercase tracking-widest hover:brightness-95 transition inline-flex items-center gap-1.5">
              <LogIn size={14} /> Sign in
            </Link>
          )}
        </div>
      </header>

      {/* ---------- Main content ---------- */}
      <main className="relative z-20 px-4 sm:px-6 lg:px-12 pb-16 pt-2">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div initial={{ y: -12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="text-[11px] uppercase tracking-[0.32em] text-brand-yellow inline-flex items-center gap-2">
            <SparkIcon size={13} /> Spin &amp; Win · The Pop Panda Club
          </motion.div>
          <h1 className="font-display text-5xl lg:text-7xl text-brand-cream mt-3 leading-none">
            <span className="inline-block" style={{ background: "linear-gradient(90deg, #FFC93C, #FF4F5A, #FFC93C)", backgroundSize: "200% 100%", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", animation: "gradientShift 4s ease-in-out infinite" }}>{wheel?.name || "Panda Daily Spin"}</span>
          </h1>
          <p className="text-brand-cream/70 mt-4 max-w-2xl mx-auto">{wheel?.description || "Every spin is a little celebration. Coins, popsicles, mysteries — the panda is waiting for you 🐼"}</p>
        </div>

        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8 mt-10 items-start max-w-6xl mx-auto">
          {/* Wheel + panda */}
          <div className="rounded-[32px] bg-white/[0.06] border border-white/12 backdrop-blur-2xl p-8 relative overflow-hidden">
            {/* Corner panda peeking */}
            <motion.div
              className="absolute top-4 left-4 z-10 pointer-events-none"
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <div className="relative">
                <PandaMascot mood={mood} size={72} />
              </div>
            </motion.div>

            {/* Encouragement label above the wheel */}
            <div className="text-center mb-4">
              <MoodMessage mood={mood} eligibility={eligibility} member={member} />
            </div>

            {wheel ? (
              <div className="flex flex-col items-center">
                <SpinWheel
                  segments={wheel.segments}
                  spinning={spinning}
                  targetIndex={targetIndex}
                  onSpin={doSpin}
                  onSpinComplete={onSpinComplete}
                  disabled={!member || !eligibility?.eligible}
                  themeColor={wheel.theme_color}
                  accentColor={wheel.accent_color}
                  soundOn={soundOn}
                  mood={mood}
                />
                <div className="mt-8 text-center min-h-[24px]">
                  {member && eligibility && !eligibility.eligible && (
                    <div data-testid="spin-blocked-reason" className="text-[#FF9EA5] text-sm">{eligibility.reason || "You can't spin right now."}</div>
                  )}
                  {member && eligibility?.eligible && (
                    <div className="text-brand-cream/75 text-sm">
                      Spins left today: <span className="text-brand-yellow font-semibold">{eligibility.spins_left_today}</span>
                      {eligibility.grants > 0 && <> · Free spin credits: <span className="text-brand-yellow font-semibold">{eligibility.grants}</span></>}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-brand-cream/60 text-center py-20">No wheels are active right now. Check back soon!</div>
            )}
          </div>

          {/* Side */}
          <div className="space-y-6">
            {!member && (
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="rounded-[28px] p-6 shadow-2xl relative overflow-hidden"
                style={{ background: "linear-gradient(135deg, #FFC93C, #FFB300)" }}
                data-testid="spin-guest-cta"
              >
                <motion.div className="absolute -top-6 -right-6 text-6xl" animate={{ rotate: [0, 10, -10, 0] }} transition={{ duration: 4, repeat: Infinity }}>🐼</motion.div>
                <div className="text-[11px] uppercase tracking-[0.28em] text-brand-charcoal/70">Members-only</div>
                <div className="font-display text-2xl mt-2 leading-tight text-brand-charcoal">Join The Pop Panda Club to spin</div>
                <p className="text-sm mt-3 text-brand-charcoal/80">Get 50 Pop Coins on signup, one free spin every day and access to exclusive rewards.</p>
                <div className="mt-5 flex gap-2">
                  <Link to="/member/register" data-testid="spin-guest-signup" className="rounded-full bg-brand-charcoal text-brand-cream px-5 py-2 text-sm hover:opacity-95">Join now</Link>
                  <Link to="/member/login" data-testid="spin-guest-signin" className="rounded-full border border-brand-charcoal/25 px-5 py-2 text-sm hover:bg-brand-charcoal/5 text-brand-charcoal">Sign in</Link>
                </div>
              </motion.div>
            )}

            {wheel?.segments && (
              <div className="rounded-[28px] bg-white/[0.06] border border-white/12 backdrop-blur-2xl p-6 relative overflow-hidden">
                <div className="text-[11px] uppercase tracking-[0.24em] text-brand-yellow flex items-center gap-2"><Gift size={13} /> Today's Prize Pool</div>
                <ul className="mt-4 space-y-2">
                  {wheel.segments.map((s, i) => (
                    <motion.li key={s.id} initial={{ x: -10, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.05 * i }} className="flex items-center gap-3 text-brand-cream text-sm">
                      <span className="h-6 w-6 rounded-full grid place-items-center border border-white/20 text-xs" style={{ background: s.color }}>{prizeEmoji(s)}</span>
                      <span className="flex-1">{s.label}</span>
                      {s.type === "coins" && s.value && <span className="font-mono text-brand-yellow">+{s.value}</span>}
                    </motion.li>
                  ))}
                </ul>
              </div>
            )}

            {member && spinHistory.length > 0 && (
              <div className="rounded-[28px] bg-white/[0.06] border border-white/12 backdrop-blur-2xl p-6" data-testid="spin-history-panel">
                <div className="text-[11px] uppercase tracking-[0.24em] text-brand-yellow flex items-center gap-2"><Clock size={13} /> Your Recent Spins</div>
                <ul className="mt-4 space-y-2 max-h-72 overflow-auto">
                  {spinHistory.slice(0, 12).map((s) => (
                    <li key={s.id} className="flex items-center gap-3 rounded-xl bg-white/5 border border-white/10 px-3 py-2">
                      {s.is_win ? <Trophy size={14} className="text-brand-yellow" /> : <span className="text-lg">🐼</span>}
                      <div className="flex-1 text-sm text-brand-cream">{s.segment_label}</div>
                      <div className="text-[10px] text-brand-cream/50">{new Date(s.created_at).toLocaleDateString()}</div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Flying coins from center to wallet */}
      <AnimatePresence>
        {flyingCoins && <FlyingCoins count={12} />}
      </AnimatePresence>

      {/* Result modal */}
      <AnimatePresence>
        {showResult && result && (
          <ResultModal
            result={result}
            onClose={() => setShowResult(false)}
            onGoToRewards={() => { setShowResult(false); nav("/member/dashboard"); }}
          />
        )}
      </AnimatePresence>

      <style>{`
        @keyframes gradientShift { 0%, 100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }
      `}</style>
    </div>
  );
}

// ------------------- Sub-components -------------------

function AnimatedBackground() {
  // Generate a stable set of floating items
  const items = Array.from({ length: 22 }).map((_, i) => ({
    id: i,
    emoji: BG_EMOJIS[i % BG_EMOJIS.length],
    left: `${(i * 37) % 100}%`,
    top: `${(i * 53) % 100}%`,
    size: 16 + ((i * 11) % 24),
    duration: 8 + ((i * 3) % 10),
    delay: (i % 6) * 0.4,
  }));
  return (
    <div className="absolute inset-0 z-10 pointer-events-none overflow-hidden">
      {/* Soft gradient blobs */}
      <motion.div className="absolute -top-40 -left-20 w-[520px] h-[520px] rounded-full bg-brand-yellow/25 blur-3xl" animate={{ x: [0, 40, 0], y: [0, 20, 0] }} transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }} />
      <motion.div className="absolute top-1/3 -right-24 w-[440px] h-[440px] rounded-full bg-[#FF4F5A]/20 blur-3xl" animate={{ x: [0, -30, 0], y: [0, 30, 0] }} transition={{ duration: 17, repeat: Infinity, ease: "easeInOut" }} />
      <motion.div className="absolute bottom-0 left-1/3 w-[380px] h-[380px] rounded-full bg-[#8B5CF6]/15 blur-3xl" animate={{ x: [0, 20, 0], y: [0, -20, 0] }} transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }} />

      {/* Floating fruits/popsicles */}
      {items.map((it) => (
        <motion.span
          key={it.id}
          className="absolute select-none"
          style={{ left: it.left, top: it.top, fontSize: it.size }}
          animate={{
            y: [-15, 15, -15],
            x: [-8, 8, -8],
            rotate: [-8, 8, -8],
            opacity: [0.35, 0.7, 0.35],
          }}
          transition={{ duration: it.duration, delay: it.delay, repeat: Infinity, ease: "easeInOut" }}
        >
          {it.emoji}
        </motion.span>
      ))}
    </div>
  );
}

function MoodMessage({ mood, eligibility, member }) {
  if (!member) return <div className="text-brand-cream/70 text-sm">🐼 Sign in to play!</div>;
  if (mood === "excited") return <div className="text-brand-yellow text-sm animate-pulse">🎯 Here we go!</div>;
  if (mood === "celebrating") return <div className="text-brand-yellow text-lg font-semibold">🎉 You won!</div>;
  if (mood === "waving") return <div className="text-brand-cream/80 text-sm">👋 So close! Try again tomorrow.</div>;
  if (eligibility && !eligibility.eligible) return <div className="text-brand-cream/60 text-sm">🐼 Rest time, Panda.</div>;
  return <div className="text-brand-cream/80 text-sm">🐼 Ready when you are!</div>;
}

function FlyingCoins({ count = 10 }) {
  // Coins fly from the center of the screen toward the top-right (wallet chip)
  return (
    <div className="fixed inset-0 z-40 pointer-events-none">
      {Array.from({ length: count }).map((_, i) => (
        <motion.div
          key={i}
          className="absolute"
          initial={{ left: "50%", top: "50%", x: -12, y: -12, scale: 0.6, opacity: 0 }}
          animate={{
            left: ["50%", "50%", "88%"],
            top: ["50%", "50%", "8%"],
            x: [-12, -12, 0],
            y: [-12, -12, 0],
            scale: [0.6, 1.2, 0.4],
            opacity: [0, 1, 0],
            rotate: [0, 180, 720],
          }}
          transition={{ duration: 1.4, delay: i * 0.06, ease: "easeInOut" }}
        >
          <div className="h-6 w-6 rounded-full grid place-items-center text-xs font-black shadow-lg" style={{ background: "radial-gradient(circle at 30% 30%, #FFF176, #FFC107)", color: "#7a5300" }}>P</div>
        </motion.div>
      ))}
    </div>
  );
}

function ResultModal({ result, onClose, onGoToRewards }) {
  const isWin = result.is_win;
  const isCoins = result.segment_type === "coins";
  const emoji = prizeEmoji(result);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md grid place-items-center p-4">
      <motion.div
        initial={{ scale: 0.6, y: 60, rotate: -5 }}
        animate={{ scale: 1, y: 0, rotate: 0 }}
        exit={{ scale: 0.85, opacity: 0 }}
        transition={{ type: "spring", stiffness: 260, damping: 20 }}
        data-testid="spin-result-modal"
        className="relative max-w-md w-full"
      >
        {/* Rotating glow behind card */}
        {isWin && (
          <motion.div
            className="absolute -inset-8 rounded-[40px] opacity-70"
            animate={{ rotate: 360 }}
            transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
            style={{ background: "conic-gradient(from 0deg, #FFC93C, #FF4F5A, #FFC93C, #FF4F5A, #FFC93C)", filter: "blur(20px)" }}
          />
        )}

        <div
          className={`relative rounded-[32px] p-8 text-center shadow-2xl overflow-hidden ${
            isWin ? "bg-gradient-to-br from-brand-yellow via-[#FFD452] to-[#FF9800] text-brand-charcoal" : "bg-gradient-to-br from-[#2d1b3d] via-[#3a2f2f] to-[#2B2B2B] text-brand-cream"
          }`}
        >
          {/* Corner sparkles */}
          {isWin && ["🎉", "✨", "🎊", "⭐"].map((e, i) => (
            <motion.span key={i} className="absolute text-3xl pointer-events-none"
              style={{
                left: i % 2 === 0 ? "8%" : "82%",
                top: i < 2 ? "8%" : "78%",
              }}
              initial={{ scale: 0, rotate: 0 }}
              animate={{ scale: [0, 1.3, 1], rotate: [0, 20, -20, 0] }}
              transition={{ delay: 0.3 + i * 0.1, duration: 0.6, repeat: Infinity, repeatDelay: 1.5 }}
            >{e}</motion.span>
          ))}

          {isWin ? (
            <>
              {/* Big emoji pop */}
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 260, damping: 12, delay: 0.15 }}
                className="text-8xl mb-2"
              >{emoji}</motion.div>

              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="text-[11px] uppercase tracking-[0.32em] text-brand-charcoal/70">You Won!</motion.div>
              <motion.h3 initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.45 }} className="font-display text-4xl text-brand-charcoal mt-2 leading-tight">{result.segment_label}</motion.h3>

              {isCoins && result.prize_meta?.coins_credited > 0 && (
                <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.6, type: "spring" }} className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand-charcoal text-brand-cream px-5 py-2">
                  <Coins size={18} className="text-brand-yellow" />
                  <span className="font-mono text-lg">+{result.prize_meta.coins_credited}</span>
                  <span className="text-brand-cream/70 text-sm">Pop Coins</span>
                </motion.div>
              )}
              {isCoins && result.prize_meta?.new_balance !== undefined && (
                <div className="mt-2 text-brand-charcoal/80 text-sm">New balance: <span className="font-semibold">{result.prize_meta.new_balance}</span></div>
              )}
              {!isCoins && result.reward_code && (
                <motion.div initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.6 }} className="mt-5 rounded-2xl bg-brand-charcoal text-brand-cream p-4">
                  <div className="text-[11px] uppercase tracking-widest text-brand-yellow">Redemption Code</div>
                  <div className="mt-1 font-mono text-2xl tracking-[0.3em]" data-testid="spin-result-code">{result.reward_code}</div>
                  <div className="mt-2 text-[11px] text-brand-cream/60">Show this at any Pop Panda outlet to claim your reward.</div>
                </motion.div>
              )}

              <div className="mt-6 flex gap-3 justify-center">
                <button onClick={onGoToRewards} data-testid="spin-view-rewards" className="rounded-full bg-brand-charcoal text-brand-cream px-5 py-2.5 hover:opacity-90">View My Rewards</button>
                <button onClick={onClose} data-testid="spin-close-result" className="rounded-full border border-brand-charcoal/25 px-5 py-2.5 text-brand-charcoal hover:bg-brand-charcoal/5">Nice!</button>
              </div>
            </>
          ) : (
            // "Better Luck Next Time" — never sad, always encouraging with a waving panda
            <>
              <motion.div initial={{ scale: 0.5, y: 40 }} animate={{ scale: 1, y: 0 }} transition={{ type: "spring", delay: 0.1 }} className="grid place-items-center">
                <PandaMascot mood="waving" size={140} />
              </motion.div>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="text-[11px] uppercase tracking-[0.32em] text-brand-yellow mt-4">Almost!</motion.div>
              <motion.h3 initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.5 }} className="font-display text-3xl mt-2">Your next spin might be luckier!</motion.h3>
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="mt-3 text-brand-cream/75 text-sm">
                Every spin gets you closer. Keep earning Pop Coins on every purchase — your next chance is just a day away.
              </motion.p>
              <motion.button initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.75 }} onClick={onClose} data-testid="spin-close-result" className="mt-6 rounded-full bg-brand-yellow text-brand-charcoal px-6 py-2.5 hover:brightness-95 inline-flex items-center gap-2">
                <span>Keep going</span> <span>🐼</span>
              </motion.button>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

function prizeEmoji(s) {
  const type = (s.type || s.segment_type || "").toLowerCase();
  if (type === "coins") return "🪙";
  if (type === "popsicle") return "🍭";
  if (type === "popcorn") return "🍿";
  if (type === "discount") return "🏷️";
  if (type === "bogo") return "🎁";
  if (type === "mystery") return "🎲";
  if (type === "festival") return "🎊";
  if (type === "nothing") return "🐼";
  return "✨";
}
